import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { loadingSelector } from '@/domain/env';
import { isImmutable } from 'immutable';
import isObject from 'lodash/isObject';

export const isChangeProps = (prev, next, propNames = []) =>
  !propNames.every((propName) => Object.is(prev[propName], next[propName]));

export const isChangeAnyProps = (prev, next) => {
  const props = Array.from(new Set([...Object.keys(prev), ...Object.keys(next)]));
  return isChangeProps(prev, next, props);
};

export const withPropsCollector = (WrappedComponent) => {
  class WithPropsCollector extends React.Component {
    shouldComponentUpdate(nextProps) {
      const { isLoadingProcess } = this.props;
      return !(isLoadingProcess && nextProps.isLoadingProcess);
    }

    render() {
      return <WrappedComponent {...this.props} />;
    }
  }

  WithPropsCollector.displayName = WrappedComponent.displayName;

  const mapStateToProps = (state) => ({
    isLoadingProcess: loadingSelector(state),
  });

  return compose(connect(mapStateToProps))(WithPropsCollector);
};

export const immutableToJs = (val) => (isImmutable(val) ? val.toJS() : val);

export const getDiffFields = (prev, next) => {
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  const propNames = Array.from(new Set([...prevKeys, ...nextKeys]));
  return propNames.reduce((res, propName) => {
    const prevProp = prev[propName];
    const nextProp = next[propName];
    const isDif = !Object.is(prevProp, nextProp);
    return isDif ? [...res, propName] : res;
  }, []);
};

export const testChangeProps = (prev, next) => {
  const diffFields = getDiffFields(prev, next);
  return diffFields.reduce((res, field) => {
    const prevData = immutableToJs(prev[field]);
    const nextData = immutableToJs(next[field]);
    const deepDiff = isObject(prevData) && isObject(nextData) ? testChangeProps(prevData, nextData) : null;
    return {
      ...res,
      [field]: {
        prev: prevData,
        current: nextData,
        _deepDiff: deepDiff,
      },
    };
  }, {});
};

export const createTestChangeParams = () => {
  let prev = {};
  return (next) => {
    const res = testChangeProps(prev, next);
    prev = next;
    return res;
  };
};

export const createMemoPropFn = (fn, propNames) => {
  let prevProps = {};
  let lastValue = null;

  return (currentProps) =>
    (...args) => {
      if (isChangeProps(prevProps, currentProps, propNames)) {
        prevProps = currentProps;
        lastValue = fn(...args);
      }
      return lastValue;
    };
};

// isChangeProps
export const createMemoFn = (prepareFn, handlerFn = (x) => x, debugName = null) => {
  let lastPrepareData = null;
  let lastValue;
  let debugCount = 0;
  return (...args) => {
    const prepareData = prepareFn(...args);
    const isUpdate = lastPrepareData === null || isChangeAnyProps(prepareData, lastPrepareData);

    if (debugName && isUpdate) {
      debugCount++;
      // eslint-disable-next-line no-console
      console.log(
        `Debug #${debugCount} MemoFn ${debugName}`,
        testChangeProps(lastPrepareData || {}, prepareData || {}),
      );
    }

    if (isUpdate) {
      lastPrepareData = prepareData;
      lastValue = handlerFn(prepareData);
    }
    return lastValue;
  };
};

export const createArgsMemoFn = (fn, debugName = null) =>
  createMemoFn(
    (...args) => args,
    (args) => fn(...args),
    debugName,
  );
